import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import { IConfigSoftwarePublisherState } from './configSoftwarePublisher.model';
import {
  deleteConfigSoftwarePublisher,
  getConfigSoftwarePublisherById,
  saveConfigSoftwarePublisher,
  searchConfigSoftwarePublisher,
} from './configSoftwarePublisher.action';
import { IConfigSoftwarePublisher } from '../../../services/master/configSoftwarePublisher/configSoftwarePublisher.model';

export const initialState: IConfigSoftwarePublisherState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const configSoftwarePublisherSlice = createSlice({
  name: 'configSoftwarePublisher',
  initialState,
  reducers: {
    clearConfigSoftwarePublisher: () => {
      return initialState;
    },
    clearConfigSoftwarePublisherMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearConfigSoftwarePublisherGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchConfigSoftwarePublisher.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchConfigSoftwarePublisher.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.lookups = { booleanLookup };
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchConfigSoftwarePublisher.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getConfigSoftwarePublisherById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getConfigSoftwarePublisherById.fulfilled.type]: (
      state,
      action: PayloadAction<IConfigSoftwarePublisher>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getConfigSoftwarePublisherById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveConfigSoftwarePublisher.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveConfigSoftwarePublisher.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveConfigSoftwarePublisher.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteConfigSoftwarePublisher.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteConfigSoftwarePublisher.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteConfigSoftwarePublisher.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const configSoftwarePublisherSelector = (state: RootState) => state.configSoftwarePublisher;

// Actions
export const {
  clearConfigSoftwarePublisher,
  clearConfigSoftwarePublisherMessages,
  clearConfigSoftwarePublisherGetById,
  setTableColumnSelection,
} = configSoftwarePublisherSlice.actions;

// The reducer
export default configSoftwarePublisherSlice.reducer;
